/**
 * Global constants for handlers
 */

// Region for all cloud functions
export const REGION = 'us-central1';

// Default timeout for cloud functions (in seconds)
export const TIMEOUT_DEFAULT = 120; // 2 minutes

// Longer timeout for batch processing
export const BATCH_TIMEOUT = 3600; // 60 minutes

// Timeout for auto batch processing
export const AUTO_BATCH_TIMEOUT = 180; // 3 minutes

// Define allowed origins for CORS
export const ALLOWED_ORIGINS = [
  'http://localhost:3000',
  'https://tradednews.com',
  'https://tradednews-af2a3.web.app',
  'https://tradednews-af2a3.firebaseapp.com'
];

// Firestore collection names
export const COLL_NEWS = 'news';
export const COLL_ITEMS = 'items';
export const COLL_ITEMS_RUNS = 'itemruns';
export const COLL_USERS = 'users';
export const COLL_PAYMENTS = 'payments';
export const COLL_BATCH_RUNS = 'batchruns';
export const COLL_TAGGED_NEWS = 'taggednews';
export const COLL_MARKET_NEWS = 'marketnews';
export const COLL_LIST_RUNS = 'listruns';
export const COLL_LIST_NEWS = 'listnews';
export const COLL_LISTS = "lists";
export const COLL_USER_NEWSLETTERS = "user_newsletters";
export const COLL_SUBSCRIPTIONS = "subscriptions";
export const COLL_VISITORS = "visitors";
export const COLL_SETTINGS = "settings";
export const COLL_SUMMARY_ITEMS = "summaryitems";
export const COLL_PROMPTS = "prompts";
export const COLL_NEWS_SOURCES = "newssources";
export const COLL_NEWS_TAGS = "newstags";

// Batch types
// export const BATCH_PROCESS_LIST_RESERVED = 0;
// export const BATCH_PROCESS_LIST_USERS = 1;
// export const BATCH_FULFILLMENT = 3;

// Batch processing constants
export const BATCH_SIZE = 10;

// News cache duration (3 hours in milliseconds)
// News updates will be skipped if the symbol was updated within this time window
export const NEWS_CACHE_DURATION_MS = 3 * 60 * 60 * 1000; // 3 hours

// Sentiment cache duration (3 hours in milliseconds)
// Sentiment analysis will be skipped if fresh sentiment exists within this time window
export const SENTIMENT_CACHE_DURATION_MS = 3 * 60 * 60 * 1000; // 3 hours

// Market summary length (in words)
export const MARKET_SUMMARY_SHORT_LENGTH = 300;

// Settings document IDs
export const DOC_SYSTEM_RUN = "systemRun";

// System user ID for reserved lists and system-level operations
export const SYSTEM_USER_ID = "SYSTEM_USER_ID";

/**
 * Batch operation timeout configurations (in milliseconds)
 * These timeouts prevent batches from hanging indefinitely
 * See TN-52 for details
 */
export const BATCH_OPERATION_TIMEOUTS = {
  // Per-batch timeouts
  HYDRATE_NEWS_SOURCES: 3 * 60 * 1000,           // 3 minutes
  HYDRATE_NEWS_CONTENT: 5 * 60 * 1000,            // 5 minutes
  HYDRATE_BATCH_FROM_DAILY_NEWS: 2 * 60 * 1000,  // 2 minutes
  HYDRATE_BATCH_WITH_CURRENT_QUOTE: 2 * 60 * 1000, // 2 minutes
  HYDRATE_BATCH_WITH_MARKET_UPDATE: 1 * 60 * 1000, // 1 minute
  HYDRATE_SYMBOLS_WITH_SENTIMENT: 15 * 60 * 1000, // 15 minutes
  REFRESH_TAGGED_NEWS: 5 * 60 * 1000,             // 5 minutes
  HYDRATE_USER_BULLETIN_CONTENT: 10 * 60 * 1000,  // 10 minutes
  MARKET_SUMMARY: 5 * 60 * 1000,                   // 5 minutes
  REFRESH_SEC_FILINGS: 3 * 60 * 1000,             // 3 minutes
  HYDRATE_AUDIO_BRIEFINGS: 5 * 60 * 1000,         // 5 minutes
  HYDRATE_USER_AUDIO_CLIPS: 3 * 60 * 1000,        // 3 minutes
  ASSIGN_BATCH_TO_USERS: 2 * 60 * 1000,           // 2 minutes

  // Per-user operation timeout (for loops processing multiple users)
  PER_USER_OPERATION: 60 * 1000,                   // 60 seconds

  // Default timeout for unknown batch types
  DEFAULT: 10 * 60 * 1000                          // 10 minutes
} as const;

/**
 * Path helper functions for nested collection structure
 */

/**
 * Build path to list runs collection within a batch
 * @param batchId The batch ID
 * @param listRunId Optional list run ID to get specific list run path
 * @returns Firestore path string
 */
export function getListRunPath(batchId: string, listRunId?: string): string {
  const basePath = `${COLL_BATCH_RUNS}/${batchId}/${COLL_LIST_RUNS}`;
  return listRunId ? `${basePath}/${listRunId}` : basePath;
}

/**
 * Build path to items collection within a list run
 * @param batchId The batch ID
 * @param listRunId The list run ID
 * @param itemId Optional item ID to get specific item path
 * @returns Firestore path string
 */
export function getListRunItemsPath(batchId: string, listRunId: string, itemId?: string): string {
  const basePath = `${COLL_BATCH_RUNS}/${batchId}/${COLL_LIST_RUNS}/${listRunId}/${COLL_ITEMS}`;
  return itemId ? `${basePath}/${itemId}` : basePath;
}

/**
 * Build path to items collection within a batch
 * @param batchId The batch ID
 * @param itemId Optional item ID to get specific item path
 * @returns Firestore path string
 */
export function getBatchItemsPath(batchId: string, itemId?: string): string {
  const basePath = `${COLL_BATCH_RUNS}/${batchId}/${COLL_ITEMS}`;
  return itemId ? `${basePath}/${itemId}` : basePath;
}
